import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, HeartHandshake } from "lucide-react";
import { MaskLine, EASE } from "../lib/anim";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 1], [1, 0]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center w-full">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full border hairline bg-white/60 backdrop-blur"
          >
            <span className="h-2 w-2 rounded-full bg-brand-green animate-pulse" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-blue">AMCares · Community Hygiene Initiative</span>
          </motion.div>

          <h1 className="font-display text-5xl md:text-7xl lg:text-[5.4rem] font-semibold tracking-tight leading-[0.95] text-brand-ink">
            <MaskLine delay={0.15}>Connecting</MaskLine>
            <MaskLine delay={0.3}>communities to</MaskLine>
            <MaskLine delay={0.45}>
              <span className="text-brand-blue italic">better health.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
            className="mt-8 text-lg md:text-xl text-brand-ink/60 max-w-xl leading-relaxed text-balance"
          >
            We assemble and distribute essential hygiene kits and health education through community clinics — with a goal of distributing 200 hygiene kits this summer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-donate-btn"
              onClick={() => go("#involved")}
              className="group bg-brand-blue text-white hover:bg-brand-bluedark transition-colors duration-300 rounded-full pl-8 pr-6 py-4 font-semibold text-base flex items-center gap-2"
            >
              Donate a Hygiene Kit
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-volunteer-btn"
              onClick={() => go("#involved")}
              className="group border hairline text-brand-ink hover:border-brand-blue hover:text-brand-blue transition-colors duration-300 rounded-full px-8 py-4 font-semibold text-base flex items-center gap-2"
            >
              <HeartHandshake className="h-4 w-4" /> Volunteer
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, ease: EASE, delay: 0.3 }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl shadow-brand-blue/10">
              <img
                src="/photo-hero.webp"
                alt="AMCares volunteer distributing hygiene kits at Lifeline Primary Care"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border hairline max-w-[210px]">
              <div className="font-display text-4xl font-semibold text-brand-green">200 kits</div>
              <div className="text-xs text-brand-ink/60 mt-1 leading-snug">our goal to distribute this summer</div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute top-1/3 -right-32 w-[36rem] h-[36rem] rounded-full bg-brand-sky/10 blur-3xl -z-0" />
      <div className="absolute -bottom-20 -left-32 w-[30rem] h-[30rem] rounded-full bg-brand-green/10 blur-3xl -z-0" />
    </section>
  );
};
